import {
    AfterViewInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    DestroyRef,
    ElementRef,
    OnDestroy,
    computed,
    inject,
    input,
    signal
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import {
    ControlContainer,
    FormControl,
    ReactiveFormsModule
} from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { AncFieldValidateDirective } from '../../form-validation/anc-field-validate.directive';
import { ancShowFieldError } from '../../form-validation/anc-show-field-error.util';
import {
    ANC_PHONE_PATTERN_ERROR_MESSAGES,
    type AncPhonePatternErrorKey,
    exceedsPhoneMaxDigits,
    hasEmptyPhoneParentheses,
    hasInvalidPhoneCharacter,
    hasMultiplePhonePlus,
    hasNoPhoneDigits,
    hasPhoneDigitBeforeParentheses,
    hasPhonePlusAfterDigit,
    isPhoneEmpty
} from '../../form-validation/anc-phone-validation.util';

/**
 * Telephone field with floating label and contextual pattern validation messages.
 * Use inside a parent `[formGroup]` / `formGroupName` with `controlName="phone"`.
 *
 * Pattern errors use legacy ngSwitch priority (same as PRODUCTION order/shipment forms).
 * Override individual messages via optional `@Input()` properties when needed.
 */
@Component({
    selector: 'lib-anc-phone-field',
    imports: [
        CommonModule,
        ReactiveFormsModule,
        TranslateModule,
        AncFieldValidateDirective
    ],
    templateUrl: './anc-phone-field.component.html',
    styleUrls: ['./anc-phone-field.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: {
        '(focusin)': 'onFocusIn()',
        '(focusout)': 'onFocusOut()',
        '(input)': 'onHostInput()',
        '(change)': 'onHostInput()'
    }
})
export class AncPhoneFieldComponent implements AfterViewInit, OnDestroy {
    label = input<string>('Contact Number');
    required = input<boolean>(false);
    controlName = input<string>('phone');
    readonly = input<boolean>(false);
    validateSubmitted = input(false, { alias: 'ancFieldValidateSubmitted' });
    requiredErrorMessage = input<string>('Contact Number is required');
    /** Replaces "Contact Number" in default contextual pattern messages. */
    patternErrorLabel = input<string>('Contact Number');

    /** Optional overrides for contextual pattern messages (empty = use default). */
    patternEmptyErrorMessage = input<string>('');
    patternInvalidCharacterErrorMessage = input<string>('');
    patternDigitBeforeParenthesesErrorMessage = input<string>('');
    patternEmptyParenthesesErrorMessage = input<string>('');
    patternMultiplePlusErrorMessage = input<string>('');
    patternPlusAfterDigitErrorMessage = input<string>('');
    patternNoDigitsErrorMessage = input<string>('');
    patternExceedsMaxDigitsErrorMessage = input<string>('');
    patternDefaultErrorMessage = input<string>('');
    minlengthErrorMessage = input<string>('');
    maxlengthErrorMessage = input<string>('');

    private readonly host = inject(ElementRef<HTMLElement>);
    private readonly destroyRef = inject(DestroyRef);
    private readonly cdr = inject(ChangeDetectorRef);
    private readonly parentForm = inject(ControlContainer, {
        optional: true,
        skipSelf: true
    });

    protected readonly focused = signal(false);
    protected readonly hasValue = signal(false);
    protected readonly floating = computed(
        () => this.focused() || this.hasValue()
    );
    /** Live phone value used by ngSwitchCase checks (matches PRODUCTION). */
    protected readonly phoneValue = signal('');

    /** Legacy helper names — used directly in template ngSwitchCase expressions. */
    protected readonly isEmpty = isPhoneEmpty;
    protected readonly hasInvalidCharacter = hasInvalidPhoneCharacter;
    protected readonly hasDigitBeforeParentheses =
        hasPhoneDigitBeforeParentheses;
    protected readonly hasEmptyParentheses = hasEmptyPhoneParentheses;
    protected readonly hasMultiplePlus = hasMultiplePhonePlus;
    protected readonly plusAfterDigit = hasPhonePlusAfterDigit;
    protected readonly hasNoDigits = hasNoPhoneDigits;
    protected readonly exceedsMaxDigits = exceedsPhoneMaxDigits;

    private observer?: MutationObserver;
    private watchedControl: FormControl | null = null;

    ngAfterViewInit(): void {
        this.syncPhoneValue();
        this.bindControl();
        Promise.resolve().then(() => this.bindControl());

        this.observer = new MutationObserver(() => this.syncPhoneValue());
        this.observer.observe(this.host.nativeElement, {
            subtree: true,
            childList: true,
            attributes: true,
            attributeFilter: ['class', 'value']
        });
    }

    ngOnDestroy(): void {
        this.observer?.disconnect();
    }

    protected onFocusIn(): void {
        this.focused.set(true);
    }

    protected onFocusOut(): void {
        this.focused.set(false);
        this.onHostInput();
    }

    protected onHostInput(): void {
        Promise.resolve().then(() => this.syncPhoneValue());
    }

    protected embeddedControl(): FormControl | null {
        const name = this.controlName();
        if (!name) {
            return null;
        }
        const group = this.parentForm?.control;
        if (!group) {
            return null;
        }
        const control = group.get(name);
        return control instanceof FormControl ? control : null;
    }

    protected fieldControl(): FormControl | null {
        return this.embeddedControl();
    }

    protected showFieldError(): boolean {
        return ancShowFieldError(this.fieldControl(), this.validateSubmitted());
    }

    protected showRequiredError(): boolean {
        return (
            this.showFieldError() &&
            !!this.requiredErrorMessage() &&
            !!this.fieldControl()?.errors?.['required']
        );
    }

    protected showMinlengthError(): boolean {
        return (
            this.showFieldError() &&
            !this.showRequiredError() &&
            !!this.fieldControl()?.errors?.['minlength']
        );
    }

    protected showMaxlengthError(): boolean {
        return (
            this.showFieldError() &&
            !this.showRequiredError() &&
            !this.fieldControl()?.errors?.['minlength'] &&
            !!this.fieldControl()?.errors?.['maxlength']
        );
    }

    /** True when `errors.pattern` is set — ngSwitch resolves the contextual message. */
    protected showPatternError(): boolean {
        return (
            this.showFieldError() &&
            !this.showRequiredError() &&
            !this.fieldControl()?.errors?.['minlength'] &&
            !this.fieldControl()?.errors?.['maxlength'] &&
            !!this.fieldControl()?.errors?.['pattern']
        );
    }

    protected patternEmptyMessage(): string {
        return this.resolvePatternMessage(
            'empty',
            this.patternEmptyErrorMessage()
        );
    }

    protected patternInvalidCharacterMessage(): string {
        return this.resolvePatternMessage(
            'invalidCharacter',
            this.patternInvalidCharacterErrorMessage()
        );
    }

    protected patternDigitBeforeParenthesesMessage(): string {
        return this.resolvePatternMessage(
            'digitBeforeParentheses',
            this.patternDigitBeforeParenthesesErrorMessage()
        );
    }

    protected patternEmptyParenthesesMessage(): string {
        return this.resolvePatternMessage(
            'emptyParentheses',
            this.patternEmptyParenthesesErrorMessage()
        );
    }

    protected patternMultiplePlusMessage(): string {
        return this.resolvePatternMessage(
            'multiplePlus',
            this.patternMultiplePlusErrorMessage()
        );
    }

    protected patternPlusAfterDigitMessage(): string {
        return this.resolvePatternMessage(
            'plusAfterDigit',
            this.patternPlusAfterDigitErrorMessage()
        );
    }

    protected patternNoDigitsMessage(): string {
        return this.resolvePatternMessage(
            'noDigits',
            this.patternNoDigitsErrorMessage()
        );
    }

    protected patternExceedsMaxDigitsMessage(): string {
        return this.resolvePatternMessage(
            'exceedsMaxDigits',
            this.patternExceedsMaxDigitsErrorMessage()
        );
    }

    protected patternDefaultMessage(): string {
        return this.resolvePatternMessage(
            'default',
            this.patternDefaultErrorMessage()
        );
    }

    protected minlengthMessage(): string {
        if (this.minlengthErrorMessage()) {
            return this.minlengthErrorMessage();
        }

        const minLength =
            this.fieldControl()?.errors?.['minlength']?.requiredLength;
        return `${this.patternErrorLabel()} is too short (Minimum ${minLength} digits are allowed)`;
    }

    protected maxlengthMessage(): string {
        if (this.maxlengthErrorMessage()) {
            return this.maxlengthErrorMessage();
        }

        const maxLength =
            this.fieldControl()?.errors?.['maxlength']?.requiredLength;
        return `${this.patternErrorLabel()} is too long (Maximum ${maxLength} digits are allowed)`;
    }

    private resolvePatternMessage(
        key: AncPhonePatternErrorKey,
        override: string
    ): string {
        if (override) {
            return override;
        }

        return ANC_PHONE_PATTERN_ERROR_MESSAGES[key].replace(
            /Contact Number/g,
            this.patternErrorLabel()
        );
    }

    private bindControl(): void {
        this.watchControl(this.embeddedControl());
    }

    private syncPhoneValue(): void {
        this.updateHasValue();
        this.phoneValue.set(this.readPhoneValue());
        this.cdr.markForCheck();
    }

    private readPhoneValue(): string {
        const control = this.fieldControl();
        const controlValue =
            control?.value != null ? String(control.value) : '';
        const input = this.host.nativeElement.querySelector(
            'input'
        ) as HTMLInputElement | null;
        const inputValue = input?.value ?? '';

        if (controlValue && inputValue && controlValue !== inputValue) {
            return inputValue.length >= controlValue.length
                ? inputValue
                : controlValue;
        }

        return controlValue || inputValue;
    }

    protected updateHasValue(): void {
        const root = this.host.nativeElement as HTMLElement;
        const input = root.querySelector('input') as HTMLInputElement | null;
        const filled = !!input?.value?.trim();
        this.hasValue.set(filled);
    }

    private watchControl(control: FormControl | null): void {
        if (!control || control === this.watchedControl) {
            return;
        }

        this.watchedControl = control;
        control.valueChanges
            ?.pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => {
                Promise.resolve().then(() => this.syncPhoneValue());
            });
        control.statusChanges
            ?.pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => this.syncPhoneValue());
    }
}
